// src/components/patient/notifications/useNotifications.js
//
// Loads the bell list with a sequence/abort guard, polls on an interval, and
// refreshes when the window regains focus. Data stays on screen during a
// refresh; mark-read updates are applied optimistically and reconciled.

import { useCallback, useEffect, useRef } from "react";

import useSequencedRequest from "../../../lib/useSequencedRequest";
import { patientNotificationsApi } from "../../../services/patientNotifications";

export const NOTIFICATION_POLL_MS = 60000;

export default function useNotifications({ limit = 8, enabled = true, pollMs = NOTIFICATION_POLL_MS } = {}) {
  const request = useSequencedRequest(null);
  const run = request.run;
  const setData = request.setData;
  const busy = useRef(false);

  const refresh = useCallback(() => {
    if (!enabled) return Promise.resolve(null);
    return run(({ signal }) => patientNotificationsApi.list({ limit }, { signal }));
  }, [enabled, limit, run]);

  useEffect(() => {
    if (!enabled) return undefined;
    refresh();
    const timer = window.setInterval(refresh, pollMs);
    const onFocus = () => refresh();
    const onVisible = () => {
      if (document.visibilityState === "visible") refresh();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [enabled, pollMs, refresh]);

  const markRead = useCallback(
    async (notification) => {
      if (!notification?.is_unread) return;
      setData((prev) =>
        prev
          ? {
              ...prev,
              unread_count: Math.max(0, Number(prev.unread_count || 0) - 1),
              items: prev.items.map((item) => (item.id === notification.id ? { ...item, is_unread: false, status: "read" } : item)),
            }
          : prev
      );
      try {
        await patientNotificationsApi.markRead(notification.id);
      } catch {
        // The next refresh reconciles.
      }
      refresh();
    },
    [refresh, setData]
  );

  const markAllRead = useCallback(async () => {
    if (busy.current) return;
    busy.current = true;
    try {
      const body = await patientNotificationsApi.markAllRead();
      setData((prev) => ({ ...(prev || {}), ...body }));
    } finally {
      busy.current = false;
    }
  }, [setData]);

  return { data: request.data, error: request.error, loading: request.loading, refresh, markRead, markAllRead };
}
